import { proxy, ref } from 'valtio';
import { devtools } from 'valtio/utils';

import * as authStore from '@cartwhl/common/stores/authStore';
import { Feedback } from '@cartwhl/common/types';

import { getStudioUrl, setMeta } from '../utils';
import { getHomepageUrl } from '../utils';

export enum CommonModalName {
  EXPLAIN_SPINS = 'EXPLAIN_SPINS',
  PURCHASE_SPINS = 'PURCHASE_SPINS',
  ENTERPRISE_CONTACT = 'ENTERPRISE_CONTACT',
}
export enum LabelerModalName {
  ANALYTICS = 'ANALYTICS',
  CONFIRM_SKIP = 'CONFIRM_SKIP',
}
export enum StudioModalName {
  ABOUT = 'ABOUT',
  BROWSE = 'BROWSE',
  DOWNLOAD = 'DOWNLOAD',
  FEEDBACK = 'FEEDBACK',
  NEW_SESSION = 'NEW_SESSION',
  RENAME_CHARACTER = 'RENAME_CHARACTER',
  SHARE = 'SHARE',
  VIDEO_UPLOAD = 'VIDEO_UPLOAD',
  IMAGE_UPLOAD = 'IMAGE_UPLOAD',
  WELCOME = 'WELCOME',
  AUTO_RIGGING = 'AUTO_RIGGING',
  RETARGETING = 'RETARGETING',
  UPLOAD_VERIFICATION = 'UPLOAD_VERIFICATION',
  READY_FOR_MORE_SPINS = 'READY_FOR_MORE_SPINS',
  FLAGGED_VIDEO = 'FLAGGED_VIDEO',
  WARN_BAD_MOTION_PREDICTED = 'WARN_BAD_MOTION_PREDICTED',
  EDIT_MOTION = 'EDIT_MOTION',
}

type CommonModalStates =
  | { type: 'EXPLAIN_SPINS'; payload?: never }
  | { type: 'PURCHASE_SPINS'; payload?: never }
  | { type: 'ENTERPRISE_CONTACT'; payload?: never };

type LabelerModalStates = {
  type: 'ANALYTICS' | 'CONFIRM_SKIP';
  payload?: never;
};
type StudioModalStates =
  | { type: 'ABOUT'; payload?: never }
  | {
      type: 'BROWSE';
      payload?: { prefillSearchTerm?: string; browseItemJobId?: string };
    }
  | { type: 'DOWNLOAD'; payload?: never }
  | { type: 'SHARE'; payload?: never }
  | { type: 'NEW_SESSION'; payload?: never }
  | { type: 'FEEDBACK'; payload: Feedback }
  | {
      type: 'RENAME_CHARACTER';
      payload: { characterID: string; characterName: string };
    }
  | {
      type: 'VIDEO_UPLOAD';
      payload?: {
        mode:
          | 'file picker'
          | 'media capture'
          | 'trimming start'
          | 'trimming end'
          | 'upload';
        initialSourceVideoBlob?: File | null;
      };
    }
  | {
      type: 'IMAGE_UPLOAD';
      payload?: {
        mode: 'file picker' | 'media capture' | 'cropping' | 'upload';
        initialSourceImageBlob?: File | null;
      };
    }
  | { type: 'WELCOME'; payload?: never }
  | {
      type: 'AUTO_RIGGING';
      payload: {
        characterID: string;
        showStep?: 0 | 1 | 2;
        autoRiggingInProgress?: boolean;
      };
    }
  | { type: 'READY_FOR_MORE_SPINS'; payload?: never }
  | {
      type: 'RETARGETING';
      payload: {
        characterID: string;
        showStep?: 0 | 1 | 2;
        retargetingInProgress?: boolean;
      };
    }
  | {
      type: 'UPLOAD_VERIFICATION';
      payload: { characterID: string };
    }
  | {
      type: 'FLAGGED_VIDEO';
      payload?: never;
    }
  | {
      type: 'WARN_BAD_MOTION_PREDICTED';
      payload?: { submit?: () => void };
    }
  | {
      type: 'EDIT_MOTION';
      payload: { jobID: string; objectID: string };
    };

export type ModalState =
  | { type: ''; payload?: never }
  | CommonModalStates
  | LabelerModalStates
  | StudioModalStates;

type NavigationStoreState = {
  hash: string;
  modal: ModalState;
  path: string;
  pathItems: string[];
  params: { [key: string]: unknown };
  search: string;
};

type NavigationStoreConfig = {
  pages: string[];
  protectedPages?: string[];
  publicOnlyPages?: string[];
  defaultRoute?: string;
};

let config: NavigationStoreConfig = {
  pages: [],
  protectedPages: [],
  publicOnlyPages: [],
  defaultRoute: '',
};

export const state = proxy<NavigationStoreState>({
  hash: '',
  modal: { type: '', payload: undefined },
  path: '',
  pathItems: [],
  params: {},
  search: '',
});

if (
  process.env.NODE_ENV === 'development' &&
  typeof window !== 'undefined' &&
  '__REDUX_DEVTOOLS_EXTENSION__' in window
) {
  devtools(state, { name: 'Navigation', enabled: true });
}

export function init({
  pages,
  protectedPages,
  publicOnlyPages,
  defaultRoute = '',
}: NavigationStoreConfig) {
  config = { pages, protectedPages, publicOnlyPages, defaultRoute };

  // Handle root path redirect on initial load
  if (window.location.pathname === '/') {
    navigate(`/${defaultRoute}`, true);
    return;
  }

  const route = getRouteFromPath(window.location.pathname);

  // Check if initial route is valid
  if (!isValidRoute(route, config.pages)) {
    navigate(`/${defaultRoute}`, true);
    return;
  }

  setLocation(`${window.location.pathname}${window.location.hash}`);

  window.addEventListener('popstate', () => {
    let newPath = `${window.location.pathname}${window.location.hash}`;
    if (newPath === '/') {
      newPath = `/${config.defaultRoute || ''}`;
    }
    if (newPath !== state.path) {
      setLocation(`${window.location.pathname}${window.location.hash}`);
    }
  });

  handleProtectedPages(route);
}

export function navigate(path: string, replace = false) {
  // Handle root path redirect
  if (path === '/') {
    path = `/${config.defaultRoute || ''}`;
  }

  const route = getRouteFromPath(path);

  // Check if route is valid before proceeding
  if (!isValidRoute(route, config.pages)) {
    // Invalid route - redirect to default route
    const defaultPath = `/${config.defaultRoute || ''}`;
    window.history.replaceState({}, document.title, defaultPath);
    setLocation(defaultPath);
    window.scrollTo(0, 0);
    return;
  }

  if (handleProtectedPages(route)) {
    return;
  }

  if (replace) {
    window.history.replaceState({}, document.title, path);
  } else {
    window.history.pushState({}, document.title, path);
  }

  setMeta({
    description: 'Turning animation on its head.',
    image: 'https://getcartwheel.com/logo256.jpg',
    title: '',
    url: path,
  });

  setLocation(path);

  window.scrollTo(0, 0);
}

export function redirectOrNavigate(path: string, replace = false) {
  const params = new URLSearchParams(window.location.search);
  const redirectTo = params.get('redirectTo');

  if (redirectTo === 'studio') {
    window.location.href = getStudioUrl();
    return;
  }
  if (redirectTo) {
    navigate(`/${redirectTo.replace('/', '')}`);
    return;
  }
  navigate(path, replace);
}

export function setParams(params: Record<string, string>) {
  // Preserve existing route parameters and merge with new query parameters
  const currentRoute = getRouteFromPath(state.path);
  const routeParams = extractRouteParams(currentRoute, config.pages);

  state.params = { ...routeParams, ...params };

  // Add query params to the URL (but not route params)
  const url = new URL(window.location.href);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  window.history.replaceState({}, document.title, url.toString());
}

// Helper function to get route parameters
export function getRouteParams(): Record<string, string> {
  const currentRoute = getRouteFromPath(state.path);
  return extractRouteParams(currentRoute, config.pages);
}

// Helper function to get a specific route parameter
export function getRouteParam(paramName: string): string | undefined {
  const routeParams = getRouteParams();
  return routeParams[paramName];
}

// Helper function to check if current route matches a pattern
export function matchesRoute(pattern: string): boolean {
  const currentRoute = getRouteFromPath(state.path);
  return matchesDynamicRoute(currentRoute, pattern) || currentRoute === pattern;
}

// Helper function to get the matching route pattern for current path
export function getCurrentRoutePattern(
  configuredRoutes: string[],
): string | null {
  const currentRoute = getRouteFromPath(state.path);

  // Check for exact match first
  if (configuredRoutes.includes(currentRoute)) {
    return currentRoute;
  }

  // Check for dynamic route matches
  for (const pattern of configuredRoutes) {
    if (matchesDynamicRoute(currentRoute, pattern)) {
      return pattern;
    }
  }

  return null;
}

// Helper type to build all valid argument combinations for setModal
// This creates a union of tuples: [type] | [type, payload] | [type, payload?]
type SetModalArgs = {
  [K in ModalState['type']]: Extract<ModalState, { type: K }> extends {
    payload?: infer P;
  }
    ? [P] extends [never]
      ? [type: K] // payload?: never → no payload parameter
      : undefined extends P
        ? [type: K, payload?: Exclude<P, undefined>] // payload?: T → optional
        : [type: K, payload: P] // payload: T → required
    : [type: K];
}[ModalState['type']];

export function setModal(...args: SetModalArgs): void {
  const [type, payload] = args;
  state.modal = { type, payload } as ModalState;
}

export function setFeedbackModal(payload: Feedback) {
  state.modal = { type: 'FEEDBACK', payload };
}

export function setRenameCharacterModal(
  characterID: string,
  characterName: string,
) {
  state.modal = {
    type: 'RENAME_CHARACTER',
    payload: { characterID, characterName },
  };
}

export function setVideoUploadModal(payload: {
  mode:
    | 'file picker'
    | 'media capture'
    | 'trimming start'
    | 'trimming end'
    | 'upload';
  initialSourceVideoBlob?: File | null;
}) {
  state.modal = {
    type: 'VIDEO_UPLOAD',
    payload: {
      ...payload,
      initialSourceVideoBlob: payload.initialSourceVideoBlob
        ? ref(payload.initialSourceVideoBlob)
        : null,
    },
  };
}

export function setImageUploadModal(payload: {
  mode: 'file picker' | 'media capture' | 'cropping' | 'upload';
  initialSourceImageBlob?: File | null;
}) {
  state.modal = {
    type: 'IMAGE_UPLOAD',
    payload: {
      ...payload,
      initialSourceImageBlob: payload.initialSourceImageBlob
        ? ref(payload.initialSourceImageBlob)
        : null,
    },
  };
}

export function closeModal() {
  state.modal = { type: '' };
}

/// Helper functions:

// Determines route from path
function getRouteFromPath(path: string): string {
  // Remove query parameters and hash
  const cleanPath = path.split('?')[0].split('#')[0];

  // Remove leading slash and return the route
  const route = cleanPath.startsWith('/') ? cleanPath.substring(1) : cleanPath;

  // Handle empty route (root path)
  return route || config.defaultRoute || '';
}

// Checks if a route matches configured pages (including dynamic routes)
function isValidRoute(route: string, configPages: string[]): boolean {
  // Direct match
  if (configPages.includes(route)) {
    return true;
  }

  // Check dynamic route patterns
  for (const page of configPages) {
    if (matchesDynamicRoute(route, page)) {
      return true;
    }
  }

  // Check if any configured page is a prefix of the route (for nested paths)
  // e.g., if config has "project" and route is "project/settings", it should match
  // But exclude requests for static files (those with file extensions)
  return configPages.some((page) => {
    if (route.startsWith(page + '/')) {
      // Don't treat paths with file extensions as nested routes
      // This allows static files to be served normally
      const pathAfterPage = route.substring(page.length + 1);
      const hasFileExtension = /\.[a-zA-Z0-9]+$/.test(pathAfterPage);
      return !hasFileExtension;
    }
    // Also check if the route is a single-level match
    return route.split('/')[0] === page;
  });
}

// Checks if a route matches a dynamic route pattern
function matchesDynamicRoute(route: string, pattern: string): boolean {
  const routeSegments = route.split('/');
  const patternSegments = pattern.split('/');

  // Must have same number of segments
  if (routeSegments.length !== patternSegments.length) {
    return false;
  }

  // Check each segment
  for (let i = 0; i < patternSegments.length; i++) {
    const patternSegment = patternSegments[i];
    const routeSegment = routeSegments[i];

    // Dynamic parameter (starts with :)
    if (patternSegment.startsWith(':')) {
      // Parameter segments match any non-empty value
      if (!routeSegment || routeSegment.trim() === '') {
        return false;
      }
      continue;
    }

    // Static segment must match exactly
    if (patternSegment !== routeSegment) {
      return false;
    }
  }

  return true;
}

// Extracts parameters from a route using a dynamic pattern
function extractRouteParams(
  route: string,
  configPages: string[],
): Record<string, string> {
  const params: Record<string, string> = {};

  // Find matching dynamic route pattern
  for (const pattern of configPages) {
    if (matchesDynamicRoute(route, pattern)) {
      const routeSegments = route.split('/');
      const patternSegments = pattern.split('/');

      // Extract parameters
      for (let i = 0; i < patternSegments.length; i++) {
        const patternSegment = patternSegments[i];
        const routeSegment = routeSegments[i];

        if (patternSegment.startsWith(':')) {
          const paramName = patternSegment.substring(1); // Remove the ':'
          params[paramName] = routeSegment;
        }
      }
      break; // Use first matching pattern
    }
  }

  return params;
}

function setLocation(path: string) {
  const url = new URL(path, window.location.href);
  state.hash = url.hash.substring(1);
  state.path = url.pathname;
  state.pathItems = state.path.split('/');
  state.search = url.search;

  // Combine query params and route params
  const queryParams = getParams(url.search);
  const route = getRouteFromPath(url.pathname);
  const routeParams = extractRouteParams(route, config.pages);

  state.params = { ...queryParams, ...routeParams };
}

function getParams(search: string) {
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};
  for (const [key, value] of params) {
    result[key] = value;
  }
  return result;
}

function handleProtectedPages(route: string): boolean {
  // Check public-only pages (both exact match, dynamic match, and prefix match)
  const isPublicOnlyPage = config.publicOnlyPages?.some((page) => {
    // Exact match
    if (route === page) return true;
    // Dynamic route match
    if (matchesDynamicRoute(route, page)) return true;
    // Prefix match for nested routes
    return route.startsWith(page + '/');
  });

  if (isPublicOnlyPage && authStore.state.authStatus === 'user') {
    window.location.href = getStudioUrl();
    return true;
  }

  // Check protected pages (both exact match, dynamic match, and prefix match)
  const isProtectedPage = config.protectedPages?.some((page) => {
    // Exact match
    if (route === page) return true;
    // Dynamic route match
    if (matchesDynamicRoute(route, page)) return true;
    // Prefix match for nested routes
    return route.startsWith(page + '/');
  });

  if (isProtectedPage && authStore.state.authStatus === 'anonymous') {
    window.location.href = getHomepageUrl(`/login?redirectTo=${route}`);
    return true;
  }

  return false;
}
